import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './basics';
import { Icon } from './icons';

const openDrawers = []; // newest last, so Esc only closes the one on top when drawers are stacked

/** Slide-in panel (or a small centred dialog with `center`). Closes on Esc or backdrop click. */
export function Drawer({ title, onClose, footer, narrow, wide, center, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const token = {};
    openDrawers.push(token);
    const onKey = (e) => e.key === 'Escape' && openDrawers[openDrawers.length - 1] === token && onClose();
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current && ref.current.focus();
    return () => {
      openDrawers.splice(openDrawers.indexOf(token), 1);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return createPortal(
    <div className={`overlay ${center ? 'center' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className={`drawer ${narrow ? 'narrow' : ''} ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <header><h2 className="grow">{title}</h2><Button variant="ghost" size="sm" icon="x" aria-label="Close" onClick={onClose} /></header>
        <div className="body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </aside>
    </div>,
    document.body,
  );
}

export function Confirm({ title, message, confirmLabel = 'Confirm', danger, busy, onConfirm, onClose }) {
  return (
    <Drawer center narrow title={title} onClose={onClose} footer={
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant={danger ? 'danger' : 'primary'} loading={busy} onClick={onConfirm}>{confirmLabel}</Button>
      </>}>
      <p className="muted">{message}</p>
    </Drawer>
  );
}

const ToastContext = createContext(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const push = useCallback((message, tone = '') => {
    const id = Math.random().toString(36).slice(2);
    setItems((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), tone === 'error' ? 6000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            {t.tone === 'success' && <Icon name="check" size={14} />} {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
